import IAnnotationView from "@/components/annotations/annotation";
import AnnotationView from "@/components/annotations/annotation.vue";
import { ImageEditorLocale } from "@/locales/localeid";
import { Annotation, ImageMetaData, ImageMetaDataDefault } from "@/models/image-meta-data";
import { VCSLevelModel } from "@/models/title-list-model";
import { ContentImageUrlModel } from "@/services/content";
import { Button, Modal, Radio } from "ant-design-vue";
import { Action, ActionItem, ArrowFunction, fmtMsg } from "commonui";
import { cloneDeep } from "lodash";
import { Component, Prop, Vue, Provide } from "vue-property-decorator";
import ContentImageSelector from "../content-image-selector/content-image-selector.vue";
import { ImageEditor, ImageEditorFlipEvent, ImageEditorRef, ImageEditorRotateEvent } from "../image-editor";
import ConversationImageContent from "@/views/conversation-builder/components/conversation-image-editor/components/conversation-image-content/conversation-image-content.vue";
import AssetImageContent from "@/views/asset-list/components/asset-image-content/asset-image-content.vue";

export enum IImageSelectorType {
	TeachingActivity = 0,
	Conversation = 1,
	Asset = 2
}
@Component({
	components: {
		Modal,
		ImageEditor,
		Action,
		ActionItem,
		Button,
		ContentImageSelector,
		RadioGroup: Radio.Group,
		Radio,
		AnnotationView,
		ConversationImageContent,
		AssetImageContent
	}
})
export default class ContentImageEditor extends Vue {
	@Prop({ default: "" }) versionGroupId!: string;
	@Prop({ default: "" }) contentId!: string;
	@Prop({ default: () => { } }) content!: { contentId: string; title: string; vcsLevelId: number; };
	@Prop({ default: 0 }) unitId!: string;
	@Prop({ default: () => [] }) units!: VCSLevelModel[];
	@Prop({ default: "" }) submitText!: string;
	@Prop({ default: "" }) cancelText!: string;
	@Prop({ default: "" }) pageId!: string;
	@Prop({ default: 0 }) imageSelectorType!: number;
	@Prop({ default: false }) callbackOnClose!: boolean; // this will return a callback in onClose event, that callback can be used to close the editor
	@Prop({ default: () => [] }) versionGroupIds!: string[];
	private metadata: ImageMetaData = cloneDeep(ImageMetaDataDefault);

	@Provide() cloneShapeFunc = this.cloneShape;
	@Provide() removeShapeFunc = this.removeShape;

	@Prop() chapterIds!: string[];

	public visible = false;

	public imageSource: string | null = null;
	public imageRelativeSource: string | null = null;
	private imageInfo: Partial<{ name: string; type: string; pageId: string; contentId?: string; }> = {};
	public isImageEditorReady: boolean = false;
	public iImageSelectorType = IImageSelectorType;
	public isAnnotationView: boolean = false;
	private pulledAnnotations: Annotation[] = [];
	public pushedAnnotations: Annotation[] = [];
	public cropperMetadata?: ImageMetaData;

	$refs!: {
		imageEditorRef: ImageEditorRef;
		annotationRef: IAnnotationView;
		assetImageContentRef: any;
	};

	public startEditing(metadata?: ImageMetaData) {
		if (metadata) {
			this.metadata = metadata;
		} else {
			this.metadata = cloneDeep(ImageMetaDataDefault);
		}
		this.open();
	}

	public reset() {
		this.visible = false;
		this.imageSource = null;
		this.imageRelativeSource = null;
		this.imageInfo = {};
		this.isAnnotationView = false;
		this.isImageEditorReady = false;
	}
	public cancel() {
		this.$emit("cancel");
		this.reset();
	}
	public close() {
		if (this.callbackOnClose) {
			this.$emit("close", this.cancel);
		} else {
			this.$emit("close");
			this.cancel();
		}
	}

	public restore() {
		this.$refs.imageEditorRef.restore();
	}

	private showWarning(callback: ArrowFunction) {
		Modal.confirm({
			okText: "Ok",
			title: fmtMsg(ImageEditorLocale.WarningMsg),
			onOk: callback
		});
	}

	private open() {
		this.imageSource = null;
		this.imageRelativeSource = null;
		this.visible = true;
		this.pushAnnotationToCanvas(this.metadata.annotations);
	}

	public uploadImage() {
		this.$refs.imageEditorRef.complete();
		this.pullAnnotationsFromCanvas();
		const metadata: ImageMetaData = this.cropperMetadata!;
		metadata.annotations = this.pulledAnnotations;
		this.$emit("uploadSuccess", {
			...this.imageInfo,
			source: this.imageSource,
			imageRelativeSource: this.imageRelativeSource,
			metaData: metadata
		});
		this.reset();
	}

	public setImageToEdit(
		url: string,
		name: string,
		type: string,
		imageDetail: ContentImageUrlModel,
		applyMetaData: boolean,
		contentId?: string
	) {
		this.pullAnnotationsFromCanvas();
		if (this.pulledAnnotations.length && this.imageSource != null && this.imageSource != url) {
			this.showWarning(() => {
				this.pushAnnotationToCanvas([]);
				this.setImageUrlAfterConfirm(url, name, type, imageDetail, applyMetaData);
			});
		} else {
			this.setImageUrlAfterConfirm(url, name, type, imageDetail, applyMetaData, contentId);
		}
	}

	private setImageUrlAfterConfirm(
		url: string,
		name: string,
		type: string,
		imageDetail: ContentImageUrlModel,
		applyMetaData: boolean,
		contentId?: string
	) {
		this.imageSource = url;
		const { pageId, url: relativeUrl } = imageDetail;
		this.imageRelativeSource = relativeUrl;
		this.imageInfo = { name, type, pageId, contentId };
		if (applyMetaData && this.metadata) {
			const { annotations, ...cropperMetadata } = cloneDeep(this.metadata);
			this.cropperMetadata = cropperMetadata;
			this.$refs.imageEditorRef.applyCropperData(cropperMetadata);
		}
	}

	public addAnnotation(event: Partial<Annotation>) {
		this.$refs.annotationRef.dropAnnotation(event);
	}

	public setAnnotationView() {
		this.pullAnnotationsFromCanvas();
		this.isAnnotationView = !this.isAnnotationView;
	}

	/**
	 * This function is to fetch the latest annotations from canvas in <AnnotationView> component.
	 */
	public pullAnnotationsFromCanvas() {
		if (
			this.imageSelectorType === IImageSelectorType.TeachingActivity ||
			this.imageSelectorType === IImageSelectorType.Asset
		) {
			this.pulledAnnotations = this.$refs.annotationRef.getAnnotations();
		}
	}

	public pushAnnotationToCanvas(annotations?: Annotation[]) {
		this.pushedAnnotations = annotations || this.metadata.annotations || [];
	}

	public saveTempCroppedData(data: { cropBoxData: Cropper.CropBoxData; cropperData: Cropper.Data; }) {
		this.cropperMetadata = data.cropperData;
	}

	public onImageEditorCanvasChange(event: Cropper.CanvasData) {
		if (
			this.imageSelectorType === IImageSelectorType.TeachingActivity ||
			this.imageSelectorType === IImageSelectorType.Asset
		) {
			this.$refs.annotationRef.updateSize(event);
		}
	}
	public onImageEditorRotate(event: ImageEditorRotateEvent) {
		if (
			this.imageSelectorType === IImageSelectorType.TeachingActivity ||
			this.imageSelectorType === IImageSelectorType.Asset
		) {
			this.$refs.annotationRef.rotate(event);
		}
	}
	public onImageEditorFlip(event: ImageEditorFlipEvent) {
		if (
			this.imageSelectorType === IImageSelectorType.TeachingActivity ||
			this.imageSelectorType === IImageSelectorType.Asset
		) {
			this.$refs.annotationRef.flip(event);
		}
	}
	public onImageEditorRestore() {
		if (
			this.imageSelectorType === IImageSelectorType.TeachingActivity ||
			this.imageSelectorType === IImageSelectorType.Asset
		) {
			this.$refs.annotationRef.restore();
		}
	}

	public onImageEditorReady() {
		this.isImageEditorReady = true;
	}

	public cloneShape() {
		this.$refs.annotationRef.cloneActiveShape();
	}
	public removeShape() {
		this.$refs.annotationRef.removeActiveShape();
	}

	unitOrComponentChange(data) {
		this.$emit("unitOrComponentChange", data);
	}
}
